import InputHelper from './InputHelper.js';
import { GAME_CONSTANTS } from './GameConstants.js';
import WorldObjectInteraction from './WorldInteraction/WorldObjectInteraction.js';
import WorldEncounterSystem from './WorldInteraction/WorldEncounterSystem.js';
import WorldMapTransition from './WorldInteraction/WorldMapTransition.js';
import WorldPlayerInteraction from './WorldInteraction/WorldPlayerInteraction.js';
import WorldInteractionHandler from './WorldInteraction/WorldInteractionHandler.js';

export default class WorldInteraction {
    constructor(scene) {
        this.scene = scene;
        
        // 初始化各个子模块
        this.objectInteraction = new WorldObjectInteraction(scene);
        this.encounterSystem = new WorldEncounterSystem(scene);
        this.mapTransition = new WorldMapTransition(scene);
        this.playerInteraction = new WorldPlayerInteraction(scene);
        this.interactionHandler = new WorldInteractionHandler(scene);
    }
    
    setupControls() {
        this.scene.cursors = InputHelper.createWASDKeys(this.scene.input.keyboard);
        this.scene.spaceKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
        this.scene.eKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
        this.scene.vKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.V);
        
        this.scene.spaceKey.on('down', () => {
            // 如果正在显示确认对话框，不处理空格键
            if (this.scene.isConfirmDialogActive) {
                return;
            }
            
            if (this.scene.worldUI && this.scene.worldUI.isDialogActive) {
                this.scene.worldUI.events.emit('spacePressed');
            } else {
                this.checkInteractions();
            }
        });
        
        this.scene.eKey.on('down', () => {
            if (!this.scene.worldUI.isDialogActive) {
                this.openMenu();
            }
        });
    }

    openMenu() {
        // 获取当前场景的 key（可能是 WorldScene、map_rado、map_hotel_1 等）
        const currentSceneKey = this.scene.scene.key;
        
        // 暂停当前场景
        this.scene.scene.pause(currentSceneKey);
        
        // 启动菜单场景，并传递当前场景的 key，以便关闭时恢复
        this.scene.scene.launch('MenuScene', {
            previousSceneKey: currentSceneKey
        });
        
        // 确保菜单场景显示在最上层
        this.scene.scene.bringToTop('MenuScene');
    }
    
    checkInteractions() {
        if (!this.scene.playerSprite) return;
        
        const playerX = this.scene.playerSprite.x;
        const playerY = this.scene.playerSprite.y;
        
        // 获取玩家当前面向方向
        let playerDirection = 'down';
        if (this.scene.worldPlayer) {
            playerDirection = this.scene.worldPlayer.getCurrentDirection();
        }
        
        // 先检查是否可以推动物体
        if (this.objectInteraction.tryPushObject(playerX, playerY, playerDirection)) {
            return;
        }
        
        // 如果是多人模式，先检查附近的玩家（组队交互）
        if (this.scene.isMultiplayer && this.scene.multiplayerPlayerManager) {
            const nearestPlayer = this.playerInteraction.getNearestPlayer(playerX, playerY, 50);
            if (nearestPlayer) {
                this.playerInteraction.showPlayerInteractionMenu(nearestPlayer);
                return;
            }
        }
        
        // 使用新的 WorldNPC 模块查找最近NPC
        // 范围限制为一格（32像素），且需要检查方向
        let nearestNPC = null;
        
        if (this.scene.worldNPC) {
            nearestNPC = this.scene.worldNPC.getNearestInteractableNPC(
                playerX, playerY, playerDirection,
                GAME_CONSTANTS.INTERACTION.NPC_INTERACTION_RADIUS
            );
        }
        
        if (nearestNPC) {
            // 检查是否是敌人（需要触发战斗）
            if (nearestNPC.data && nearestNPC.data.isEnemy) {
                this.encounterSystem.triggerEnemyBattle(nearestNPC);
                return;
            }
            
            this.interactionHandler.interactWithNPC(nearestNPC);
            return;
        }
        
        // 检查与可交互物体的交互
        for (const obj of this.scene.interactiveObjects) {
            const distance = Phaser.Math.Distance.Between(
                playerX, playerY,
                obj.x, obj.y
            );
            
            if (distance < 25) {
                if (obj.type !== 'chest') {
                    this.scene.worldUI.showDialog(obj.name, obj.messages);
                }
                return;
            }
        }
        
        // 没有可交互目标时静默返回
        return;
    }
    
    /**
     * 检查随机遇敌（委托给遇敌系统）
     */
    checkRandomEncounter() {
        this.encounterSystem.checkRandomEncounter();
    }
    
    /**
     * 检查地图转换（委托给地图转换模块）
     */
    checkMapTransitions() {
        this.mapTransition.checkMapTransitions();
    }
    
    /**
     * 触发敌人战斗（委托给遇敌系统）
     * @param {Object} enemyNPC - 敌人NPC对象
     */
    triggerEnemyBattle(enemyNPC) {
        this.encounterSystem.triggerEnemyBattle(enemyNPC);
    }

    /**
     * 重置固定敌人战的触发锁（战斗结束恢复场景时调用）
     */
    resetBattleTrigger() {
        if (this.encounterSystem) {
            this.encounterSystem.isTriggeringBattle = false;
            this.encounterSystem.dismissBattleIntroMessage();
        }
    }
}
